import type { SignalDataEndpoint } from "components/Signals/SignalsModels";
import { reaction } from "mobx";
import { AuthStatus, AuthStore } from "stores/AuthStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";

export const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;
export const DEFAULT_MAX_VALUES_PER_ENDPOINT = 5;

type CacheScope = "primaryData" | "comparePrev" | string;

type CacheEntry = {
    value: unknown;
    cachedAtMs: number;
    expiresAtMs: number;
};

type EndpointCacheBucket = Map<string, CacheEntry>;

@AcxStore
class SignalsEndpointCacheStore {
    private readonly ttlMs: number = DEFAULT_CACHE_TTL_MS;
    private readonly maxValuesPerEndpoint: number =
        DEFAULT_MAX_VALUES_PER_ENDPOINT;

    private endpointCaches: Map<string, EndpointCacheBucket> = new Map();

    constructor(rootStore: IRootStore) {
        reaction(
            () => ({
                effectiveOrgId: rootStore.getStore(AuthStore).effectiveOrgId,
                effectiveUserId: rootStore.getStore(AuthStore).effectiveUserId,
                selectedOrgId:
                    rootStore.getStore(AuthStore).orgStore?.selectedOrganization
                        ?.id,
                status: rootStore.getStore(AuthStore).status,
            }),
            (currentScope, previousScope) => {
                if (
                    currentScope.status !== AuthStatus.Initialized ||
                    !previousScope
                ) {
                    return;
                }

                const scopeChanged =
                    currentScope.effectiveOrgId !==
                        previousScope.effectiveOrgId ||
                    currentScope.effectiveUserId !==
                        previousScope.effectiveUserId ||
                    currentScope.selectedOrgId !== previousScope.selectedOrgId;

                if (scopeChanged) {
                    this.clearAll();
                }
            },
        );
    }

    get<T>({
        endpoint,
        key,
        scope,
    }: {
        endpoint: SignalDataEndpoint | string;
        key: string;
        scope?: CacheScope;
    }): T | undefined {
        const endpointKey = `${endpoint}`;
        const bucket = this.endpointCaches.get(endpointKey);
        if (!bucket) return undefined;

        this.pruneExpiredEntries(bucket);

        const scopedKey = this.getScopedKey(key, scope);
        const entry = bucket.get(scopedKey);
        if (!entry) return undefined;

        if (entry.expiresAtMs <= Date.now()) {
            bucket.delete(scopedKey);
            if (bucket.size === 0) {
                this.endpointCaches.delete(endpointKey);
            }
            return undefined;
        }

        // Move to the end to maintain LRU order.
        bucket.delete(scopedKey);
        bucket.set(scopedKey, entry);

        return entry.value as T;
    }

    set<T>({
        endpoint,
        key,
        value,
        scope,
    }: {
        endpoint: SignalDataEndpoint | string;
        key: string;
        value: T;
        scope?: CacheScope;
    }): void {
        const endpointKey = `${endpoint}`;
        const bucket = this.getOrCreateBucket(endpointKey);
        const scopedKey = this.getScopedKey(key, scope);

        this.pruneExpiredEntries(bucket);

        if (bucket.has(scopedKey)) {
            bucket.delete(scopedKey);
        }

        const now = Date.now();
        bucket.set(scopedKey, {
            value,
            cachedAtMs: now,
            expiresAtMs: now + this.ttlMs,
        });

        this.enforceMaxEntries(bucket);
    }

    clearEndpoint(endpoint: SignalDataEndpoint | string): void {
        this.endpointCaches.delete(`${endpoint}`);
    }

    /**
     * Clears every bucket for a base endpoint id, including its query-string variants (e.g.
     * "PublishedGroupedModelResults?classifierGroupId=..."). Buckets are keyed by the full dataEndpoint, so
     * use this when the data behind a family of endpoints changed (e.g. a model was unpublished) and all
     * cached variants — across charts and filter states — must be invalidated.
     */
    clearEndpointsByBaseId(baseId: SignalDataEndpoint | string): void {
        const base = `${baseId}`;
        const prefix = `${base}?`;
        for (const endpointKey of [...this.endpointCaches.keys()]) {
            if (endpointKey === base || endpointKey.startsWith(prefix)) {
                this.endpointCaches.delete(endpointKey);
            }
        }
    }

    clearAll(): void {
        this.endpointCaches.clear();
    }

    private getOrCreateBucket(endpoint: string): EndpointCacheBucket {
        let bucket = this.endpointCaches.get(endpoint);
        if (!bucket) {
            bucket = new Map();
            this.endpointCaches.set(endpoint, bucket);
        }

        return bucket;
    }

    private getScopedKey(key: string, scope?: CacheScope): string {
        return `${scope ?? "default"}::${key}`;
    }

    private pruneExpiredEntries(bucket: EndpointCacheBucket): void {
        const now = Date.now();
        for (const [cacheKey, entry] of bucket.entries()) {
            if (entry.expiresAtMs <= now) {
                bucket.delete(cacheKey);
            }
        }
    }

    private enforceMaxEntries(bucket: EndpointCacheBucket): void {
        while (bucket.size > this.maxValuesPerEndpoint) {
            const oldestKey = bucket.keys().next().value;
            if (oldestKey === undefined) {
                break;
            }
            bucket.delete(oldestKey);
        }
    }
}

export default SignalsEndpointCacheStore;
